import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';
import DeviceSerialConsole from '../components/DeviceSerialConsole';


// La scheda monta 6 canali PT100 (TEMP-01..TEMP-06). Le sonde XY-MD04 sul bus
// RS485 proseguono la numerazione: ogni sonda porta una temperatura e
// un'umidita', quindi la prima del bus e' TEMP-07 + HUM-01, la seconda
// TEMP-08 + HUM-02. Il firmware del Pulse v4 (MB-23-09-26 e seguenti) ne
// interroga fino a 32 su quattro bus, da cui TEMP-38 e HUM-32 come tetto.
// I canali mostrati restano quelli che il device ha davvero trasmesso.
const PT100_CHANNEL_COUNT = 6;
const MAX_MODBUS_PROBES = 32;

const DEFAULT_CHANNEL_COUNT = PT100_CHANNEL_COUNT;
const MAX_CHANNEL_COUNT = PT100_CHANNEL_COUNT + MAX_MODBUS_PROBES;

// Di base si mostra una sola umidita': le altre compaiono quando il device
// dichiara di avere altre sonde sul bus. Mostrarle tutte e quattro sempre
// riempirebbe la pagina di canali che sulla maggior parte delle schede non
// esistono.
const DEFAULT_HUMIDITY_COUNT = 1;

// Canali non PT100 gestiti dalla scheda. L'elenco resta esplicito e non
// dedotto dalle misure, cosi' l'umidita' e' censibile anche su un device
// appena accoppiato che non ha ancora trasmesso nulla.
const buildExtraChannels = (count = DEFAULT_HUMIDITY_COUNT) =>
  Array.from({ length: count }, (_, index) => ({
    sensor_id: `HUM-${String(index + 1).padStart(2, '0')}`,
    label: `UM${index + 1}`,
    unit: '%RH',
    sensor_type: 'humidity',
  }));

const CELL_TYPES = [
  { value: '', label: 'Non impostato' },
  { value: 'frigo_positivo', label: 'Frigo positivo' },
  { value: 'congelatore', label: 'Congelatore' },
  { value: 'cella_latticini', label: 'Cella latticini' },
  { value: 'cella_carne', label: 'Cella carne' },
  { value: 'cella_pesce', label: 'Cella pesce' },
  { value: 'cella_verdure', label: 'Cella verdure' },
  { value: 'ambiente', label: 'Ambiente' },
];

const normalizeSensorId = (sensorId = '') => {
  const raw = String(sensorId || '').trim();

  // TEMP-1 / TEMP-01 -> TEMP-01, e lo stesso per HUM-*, cosi' un id scritto
  // senza zero iniziale dal firmware resta confrontabile con quelli del form.
  const match = raw.match(/^(TEMP|HUM)-0?(\d+)$/i);
  if (match) {
    const n = Number(match[2]);
    if (Number.isFinite(n) && n > 0) {
      return `${match[1].toUpperCase()}-${String(n).padStart(2, '0')}`;
    }
  }

  return raw;
};

// Unita' di misura dell'offset di taratura: i PT100 e le temperature delle
// sonde del bus sono in gradi, le umidita' in punti percentuali di umidita'
// relativa. Si guarda il prefisso e non un elenco fisso, cosi' vale anche per
// HUM-02 e seguenti.
const channelUnit = (sensorId) =>
  normalizeSensorId(sensorId).startsWith('HUM-') ? '%RH' : '°C';

const buildDefaultChannels = (
  count = DEFAULT_CHANNEL_COUNT,
  humidityCount = DEFAULT_HUMIDITY_COUNT
) => {
  const temperatureChannels = Array.from({ length: count }, (_, index) => {
    const channelNumber = index + 1;

    // Oltre il sesto non e' piu' un PT100 a bordo ma la temperatura di una
    // sonda del bus: l'etichetta lo dice, altrimenti in cantiere TEMP-07 si
    // cerca su un morsetto che non esiste.
    const isModbusProbe = channelNumber > PT100_CHANNEL_COUNT;

    return {
      sensor_id: `TEMP-${String(channelNumber).padStart(2, '0')}`,
      label: isModbusProbe
        ? `MB${channelNumber - PT100_CHANNEL_COUNT}`
        : `CH${channelNumber}`,
      enabled: true,
      machineRef: '',
      departmentRef: '',
      cell_type: '',
      calibration_offset: 0,
      calibration_enabled: true,
    };
  });

  const extraChannels = buildExtraChannels(humidityCount).map((channel) => ({
    sensor_id: channel.sensor_id,
    label: channel.label,
    enabled: true,
    machineRef: '',
    departmentRef: '',
    cell_type: '',
    calibration_offset: 0,
    calibration_enabled: true,
  }));

  return [...temperatureChannels, ...extraChannels];
};

/*
  Numeri di canale gia' visti su questo device, fra le misure arrivate e i
  canali salvati. Serve a far comparire TEMP-07 o HUM-02 appena il device
  dichiara di averli, senza mostrarli su tutte le altre schede.
*/
const detectedChannelNumbers = (device, prefix) => {
  const pattern = new RegExp(`^${prefix}-(\\d+)$`);

  const fromList = (list) =>
    Array.isArray(list)
      ? list
          .map((item) => {
            const match = normalizeSensorId(item?.sensor_id).match(pattern);
            return match ? Number(match[1]) : 0;
          })
          .filter((value) => Number.isFinite(value) && value > 0)
      : [];

  return [
    ...fromList(device?.last_measures),
    ...fromList(device?.channels),
  ];
};

const getDeviceChannelCount = (device) => {
  const detectedMax = Math.max(
    DEFAULT_CHANNEL_COUNT,
    ...detectedChannelNumbers(device, 'TEMP')
  );

  return Math.min(detectedMax, MAX_CHANNEL_COUNT);
};

const getDeviceHumidityCount = (device) => {
  const detectedMax = Math.max(
    DEFAULT_HUMIDITY_COUNT,
    ...detectedChannelNumbers(device, 'HUM')
  );

  return Math.min(detectedMax, MAX_MODBUS_PROBES);
};

// Un canale gia' salvato sul device, riportato nella forma usata dal form.
const channelFromExisting = (existing) => ({
  sensor_id: normalizeSensorId(existing?.sensor_id),
  label: existing?.label || normalizeSensorId(existing?.sensor_id),
  enabled: existing?.enabled !== false,
  machineRef:
    typeof existing?.machineRef === 'string'
      ? existing.machineRef
      : existing?.machineRef?._id || '',
  departmentRef:
    typeof existing?.departmentRef === 'string'
      ? existing.departmentRef
      : existing?.departmentRef?._id || '',
  cell_type: existing?.cell_type || '',
  calibration_offset:
    existing?.calibration_offset !== undefined && existing?.calibration_offset !== null
      ? Number(existing.calibration_offset)
      : 0,
  calibration_enabled: existing?.calibration_enabled !== false,
});

const normalizeChannelsForForm = (device) => {
  const count = getDeviceChannelCount(device);
  const humidityCount = getDeviceHumidityCount(device);
  const defaults = buildDefaultChannels(count, humidityCount);
  const existingChannels = Array.isArray(device?.channels) ? device.channels : [];

  const normalized = defaults.map((defaultChannel) => {
    const existing = existingChannels.find(
      (channel) => normalizeSensorId(channel?.sensor_id) === defaultChannel.sensor_id
    );

    if (!existing) return defaultChannel;

    return {
      ...defaultChannel,
      sensor_id: defaultChannel.sensor_id,
      enabled: existing.enabled !== false,
      machineRef:
        typeof existing.machineRef === 'string'
          ? existing.machineRef
          : existing.machineRef?._id || '',
      departmentRef:
        typeof existing.departmentRef === 'string'
          ? existing.departmentRef
          : existing.departmentRef?._id || '',
      cell_type: existing.cell_type || '',
      calibration_offset:
        existing.calibration_offset !== undefined &&
        existing.calibration_offset !== null
          ? Number(existing.calibration_offset)
          : 0,
      calibration_enabled: existing.calibration_enabled !== false,
    };
  });

  /*
    Il form salva esattamente i canali che mostra: un canale gia' presente sul
    device ma non previsto qui (un id che il firmware manda e il gestionale non
    conosce) sparirebbe al primo salvataggio, insieme alla sua macchina e alla
    sua taratura. Quindi si riportano in coda invece di perderli.
  */
  const known = new Set(normalized.map((channel) => channel.sensor_id));

  const unknown = existingChannels
    .filter((channel) => {
      const sensorId = normalizeSensorId(channel?.sensor_id);
      return sensorId && !known.has(sensorId);
    })
    .map(channelFromExisting);

  return [...normalized, ...unknown];
};


export default function DispositiviSmart() {
  const navigate = useNavigate();

  // La console seriale e' riservata all'admin (lato server la rotta
  // /console e' protetta allo stesso modo: questo nasconde solo il tab).
  const isAdmin = localStorage.getItem('role') === 'admin';

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [devicesError, setDevicesError] = useState('');

  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceTab, setDeviceTab] = useState('assign');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  const [machines, setMachines] = useState([]);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [departments, setDepartments] = useState([]);

  const [form, setForm] = useState({
    machineRef: '',
    channels: buildDefaultChannels(),
    installation_site: '',
    room: '',
    fridge_serial: '',
    installed_by: '',
    installation_notes: '',
  });

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmText: 'Conferma',
    danger: false,
    onConfirm: null,
  });


  const fetchMachines = async () => {
    try {
      setLoadingMachines(true);

      const res = await authFetch('/api/machines');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore nel recupero delle macchine');
      }

      setMachines(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Errore fetch machines:', error);
    } finally {
      setLoadingMachines(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await authFetch('/api/departments');
      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || 'Errore nel recupero reparti');
      }

      setDepartments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Errore fetch departments:', error);
      setDepartments([]);
    }
  };

  const fetchDevices = async () => {
    try {
      setLoadingDevices(true);
      setDevicesError('');

      const res = await authFetch('/api/iot/devices');
      if (!res || !res.ok) {
        throw new Error('Errore nel recupero dei dispositivi');
      }

      const data = await res.json();
      setDevices(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Errore fetch devices:', error);
      setDevicesError('Impossibile recuperare i dispositivi.');
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    fetchDevices();
    fetchMachines();
    fetchDepartments();
  }, []);

  const latestDevice = useMemo(() => {
    if (!devices.length) return null;

    return [...devices].sort((a, b) => {
      const dateA = a.last_seen_at ? new Date(a.last_seen_at).getTime() : 0;
      const dateB = b.last_seen_at ? new Date(b.last_seen_at).getTime() : 0;
      return dateB - dateA;
    })[0];
  }, [devices]);

  const openAssignForm = (device) => {
    const channels = normalizeChannelsForForm(device);

    setSelectedDevice(device);
    // Aprendo un altro device si riparte sempre dall'associazione, altrimenti
    // resterebbe aperta la console del dispositivo precedente.
    setDeviceTab('assign');
    setSaveMessage('');
    setSaveError('');
    setForm({
      machineRef: device.machineRef?._id || '',
      channels,
      installation_site: device.installation_site || '',
      room: device.room || '',
      fridge_serial: device.fridge_serial || '',
      installed_by: device.installed_by || '',
      installation_notes: device.installation_notes || '',
    });
  };

  const closeAssignForm = () => {
    setSelectedDevice(null);
    setSaveMessage('');
    setSaveError('');
    setForm({
      machineRef: '',
      channels: buildDefaultChannels(),
      installation_site: '',
      room: '',
      fridge_serial: '',
      installed_by: '',
      installation_notes: '',
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleChannelChange = (index, field, value) => {
    setForm((prev) => {
      const nextChannels = [...prev.channels];
      const currentChannel = nextChannels[index];

      if (!currentChannel) return prev;

      let nextChannel = {
        ...currentChannel,
        [field]: value,
      };

      if (field === 'machineRef') {
        const selectedMachine = machines.find((machine) => machine._id === value);
        const departmentId =
          selectedMachine?.departmentRef?._id ||
          selectedMachine?.departmentRef ||
          '';

        nextChannel = {
          ...nextChannel,
          machineRef: value,
          departmentRef: departmentId,
        };
      }

      if (field === 'enabled') {
        nextChannel = {
          ...nextChannel,
          enabled: Boolean(value),
        };
      }

      nextChannels[index] = nextChannel;

      return {
        ...prev,
        channels: nextChannels,
        machineRef:
          nextChannels.find((channel) => channel.enabled && channel.machineRef)
            ?.machineRef || '',
      };
    });
  };



  

  const getMachineDepartmentLabel = (machineId) => {
    const machine = getMachineByRef(machineId);
    const departmentName = getDepartmentNameFromMachine(machine);

    return departmentName || 'Reparto non associato';
  };


  const getRefId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value._id || '';
  };

  const getMachineByRef = (machineRef) => {
    const machineId = getRefId(machineRef);
    if (!machineId) return null;

    return machines.find((machine) => String(machine._id) === String(machineId)) || null;
  };


  const getDepartmentByRef = (departmentRef) => {
    const departmentId = getRefId(departmentRef);
    if (!departmentId) return null;

    return (
      departments.find(
        (department) => String(department._id) === String(departmentId)
      ) || null
    );
  };

const getDepartmentNameFromMachine = (machine) => {
  if (!machine) return '';

  const department = machine.departmentRef;

  if (!department) {
    return machine.reparto || '';
  }

  if (typeof department === 'object' && department.name) {
    return department.name;
  }

  const fullDepartment = getDepartmentByRef(department);

  return fullDepartment?.name || machine.reparto || '';
};

const getDeviceDepartmentsLabel = (device) => {
  const departmentNames = new Set();

  if (Array.isArray(device?.channels)) {
    device.channels
      .filter((channel) => channel.enabled !== false)
      .forEach((channel) => {
        const machine = getMachineByRef(channel.machineRef);
        const departmentName = getDepartmentNameFromMachine(machine);

        if (departmentName) {
          departmentNames.add(departmentName);
        }
      });
  }

  if (departmentNames.size === 0 && device?.machineRef) {
    const machine =
      typeof device.machineRef === 'object'
        ? device.machineRef
        : getMachineByRef(device.machineRef);

    const departmentName = getDepartmentNameFromMachine(machine);

    if (departmentName) {
      departmentNames.add(departmentName);
    }
  }

  return departmentNames.size > 0
    ? Array.from(departmentNames).join(', ')
    : '-';
};

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDevice) return;

    try {
      setSaving(true);
      setSaveMessage('');
      setSaveError('');

      const res = await authFetch(
        `/api/iot/devices/${selectedDevice.device_id}/assign`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...form,
            channels: form.channels.map((channel) => ({
              ...channel,
              sensor_id: normalizeSensorId(channel.sensor_id),
              machineRef: channel.enabled ? channel.machineRef || null : null,
              departmentRef: channel.enabled ? channel.departmentRef || null : null,
              cell_type: channel.enabled ? channel.cell_type || '' : '',
              calibration_offset: Number(channel.calibration_offset || 0),
              calibration_enabled: channel.calibration_enabled !== false,
            })),
          }),
        }
      );

      const data = await res.json().catch(() => null);

      if (!res || !res.ok) {
        throw new Error(data?.message || "Errore nel salvataggio");
      }

      setSaveMessage('Associazione dispositivo salvata correttamente.');
      await fetchDevices();
      setSelectedDevice((prev) => {
        if (!prev) return null;
        return {
          ...prev,
          ...form,
        };
      });

    } catch (error) {
      console.error('Errore assign device:', error);
      setSaveError(
        error?.message || "Impossibile salvare l'associazione del dispositivo."
      );
    } finally {
      setSaving(false);
    }
  };


  const openConfirmModal = ({ title, message, confirmText, danger, onConfirm }) => {
    setConfirmModal({
      open: true,
      title,
      message,
      confirmText: confirmText || 'Conferma',
      danger: Boolean(danger),
      onConfirm,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({
      ...prev,
      open: false,
      onConfirm: null,
    }));
  };

  const runConfirmedAction = async () => {
    const action = confirmModal.onConfirm;
    closeConfirmModal();

    if (typeof action === 'function') {
      await action();
    }
  };


  // riconfigurazione ESP32-S3
const handleUnassign = (device) => {
  openConfirmModal({
    title: 'Disassociare dispositivo?',
    message: `Vuoi davvero disassociare il dispositivo ${device.device_id}? Lo storico telemetrico non verrà eliminato.`,
    confirmText: 'Disassocia',
    danger: false,
    onConfirm: async () => {
      try {
        setSaving(true);
        setSaveMessage('');
        setSaveError('');

        const res = await authFetch(`/api/iot/devices/${device.device_id}/unassign`, {
          method: 'PUT',
        });

        const data = await res.json().catch(() => null);

        if (!res || !res.ok) {
          throw new Error(data?.message || 'Errore nella disassociazione');
        }

        setSaveMessage('Dispositivo disassociato correttamente.');
        await fetchDevices();

        if (selectedDevice?.device_id === device.device_id) {
          closeAssignForm();
        }
      } catch (error) {
        console.error('Errore unassign device:', error);
        setSaveError(error?.message || 'Impossibile disassociare il dispositivo.');
      } finally {
        setSaving(false);
      }
    },
  });
};
const handleReconfigure = (device) => {
  openConfirmModal({
    title: 'Riconfigurare dispositivo?',
    message: `Vuoi avviare la riconfigurazione del dispositivo ${device.device_id}? Il dispositivo verrà disassociato e, al prossimo contatto con il server, entrerà in modalità configurazione Wi-Fi.`,
    confirmText: 'Riconfigura',
    danger: true,
    onConfirm: async () => {
      try {
        setSaving(true);
        setSaveMessage('');
        setSaveError('');

        const res = await authFetch(`/api/iot/devices/${device.device_id}/reconfigure`, {
          method: 'POST',
        });

        const data = await res.json().catch(() => null);

        if (!res || !res.ok) {
          throw new Error(data?.message || 'Errore nella riconfigurazione');
        }

        setSaveMessage('Riconfigurazione richiesta correttamente.');
        await fetchDevices();

        if (selectedDevice?.device_id === device.device_id) {
          closeAssignForm();
        }
      } catch (error) {
        console.error('Errore reconfigure device:', error);
        setSaveError(error?.message || 'Impossibile avviare la riconfigurazione.');
      } finally {
        setSaving(false);
      }
    },
  });
};

const handleReboot = (device) => {
  openConfirmModal({
    title: 'Riavviare dispositivo?',
    message: `Vuoi riavviare il dispositivo ${device.device_id}?`,
    confirmText: 'Riavvia',
    danger: false,
    onConfirm: async () => {
      try {
        setSaving(true);
        setSaveMessage('');
        setSaveError('');

        const res = await authFetch(`/api/iot/devices/${device.device_id}/reboot`, {
          method: 'POST',
        });

        const data = await res.json().catch(() => null);

        if (!res || !res.ok) {
          throw new Error(data?.message || 'Errore nella richiesta di riavvio');
        }

        setSaveMessage('Riavvio richiesto correttamente.');
        await fetchDevices();
      } catch (error) {
        console.error('Errore reboot device:', error);
        setSaveError(error?.message || 'Impossibile richiedere il riavvio.');
      } finally {
        setSaving(false);
      }
    },
  });
};

// end 


const renderMeasures = (measures) => {
  const filteredMeasures = Array.isArray(measures)
    ? measures.filter((measure) =>
        /^(TEMP|HUM)-\d+$/.test(normalizeSensorId(measure?.sensor_id))
      )
    : [];

  if (filteredMeasures.length === 0) {
    return (
      <p className="text-sm text-gray-500">Nessun sensore rilevato.</p>
    );
  }

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {filteredMeasures.map((measure, index) => {
        const safeMeasure = measure || {};
        return (
          <div
            key={`${safeMeasure.sensor_id || 'sensor'}-${index}`}
            className="rounded-xl border border-gray-200 bg-white p-4"
          >
            <p className="text-sm font-semibold text-gray-800">
              {safeMeasure.sensor_id || 'Sensore'}
            </p>

            <p className="mt-1 text-sm text-gray-600">
              <span className="font-medium">Tipo:</span>{' '}
              {safeMeasure.sensor_type || '-'}
            </p>

            <p className="mt-1 text-sm text-gray-600">
              <span className="font-medium">Valore:</span>{' '}
              {safeMeasure.value !== undefined && safeMeasure.value !== null
                ? String(safeMeasure.value)
                : '-'}{' '}
              {safeMeasure.unit || ''}
            </p>

            <p className="mt-1 text-sm text-gray-500">
              <span className="font-medium">Qualità:</span>{' '}
              {safeMeasure.quality_flag || '-'}
            </p>
          </div>
        );
      })}
    </div>
  );
};


  const getMachineCustomerLabel = (machine) => {
    if (!machine?.customerUserId) return '-';

    const c = machine.customerUserId;
    return `${c.nome || ''} ${c.cognome || ''}`.trim() || c.username || c.matricola || '-';
  };


  const getPairingBadge = (status) => {
    switch (status) {
      case 'paired':
        return {
          label: 'Associato',
          className: 'bg-emerald-100 text-emerald-700',
        };

      case 'provisioning':
        return {
          label: 'In riconfigurazione',
          className: 'bg-amber-100 text-amber-700',
        };

      case 'unpaired':
        return {
          label: 'Disassociato',
          className: 'bg-slate-100 text-slate-700',
        };

      case 'error':
        return {
          label: 'Errore',
          className: 'bg-red-100 text-red-700',
        };

      case 'never_paired':
      default:
        return {
          label: 'Mai associato',
          className: 'bg-gray-100 text-gray-600',
        };
    }
  };

  const getLoraCommandBadge = (device) => {
    if (device?.last_lora_ack_at) {
      return {
        label: 'ACK ricevuto',
        className: 'bg-emerald-100 text-emerald-700',
      };
    }

    if (device?.last_lora_downlink_at) {
      return {
        label: 'Inviato via gateway',
        className: 'bg-indigo-100 text-indigo-700',
      };
    }

    if (device?.last_lora_command_id) {
      return {
        label: 'Comando pending',
        className: 'bg-amber-100 text-amber-700',
      };
    }

    return {
      label: 'Nessun comando LoRa',
      className: 'bg-slate-100 text-slate-700',
    };
  };


  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-6xl mx-auto px-4 pt-10 space-y-6">
        <Card className="shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Dispositivi smart
              </h1>
              <p className="text-sm text-gray-500 mt-2">
                Visualizza i dispositivi registrati, individua l’ultimo device
                rilevato e completa l’associazione impiantistica.
              </p>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={fetchDevices}
                className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition"
              >
                Aggiorna
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
              >
                Torna alla dashboard
              </button>
            </div>
          </div>
        </Card>

        <Card className="shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Ultimo device rilevato
          </h2>

          {loadingDevices && (
            <p className="text-sm text-gray-500">Caricamento dispositivi...</p>
          )}

          {!loadingDevices && devicesError && (
            <p className="text-sm text-red-600">{devicesError}</p>
          )}

          {!loadingDevices && !devicesError && !latestDevice && (
            <p className="text-sm text-gray-500">Nessun dispositivo rilevato.</p>
          )}

          {!loadingDevices && !devicesError && latestDevice && (
            <div className="border border-cyan-200 bg-cyan-50 rounded-xl p-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-cyan-700">
                    Device ID
                  </p>
                  <p className="mt-1 text-lg font-semibold text-gray-800">
                    {latestDevice.device_id}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-cyan-700">
                    Ultimo contatto
                  </p>
                  <p className="mt-1 text-sm text-gray-700">
                    {latestDevice.last_seen_at
                      ? new Date(latestDevice.last_seen_at).toLocaleString()
                      : '-'}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-cyan-700">
                    IP
                  </p>
                  <p className="mt-1 text-sm text-gray-700">
                    {latestDevice.ip_address || '-'}
                  </p>
                </div>

                  <div>
                    <p className="text-xs font-semibold uppercase text-cyan-700">
                      Pairing
                    </p>
                    {(() => {
                      const pairingBadge = getPairingBadge(latestDevice.pairing_status);
                      return (
                        <span
                          className={`mt-1 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${pairingBadge.className}`}
                        >
                          {pairingBadge.label}
                        </span>
                      );
                    })()}
                  </div>
                  
                  <div>
                    <p className="text-xs font-semibold uppercase text-cyan-700">
                      Stato LoRa
                    </p>
                    {(() => {
                      const loraBadge = getLoraCommandBadge(latestDevice);
                      return (
                        <span
                          className={`mt-1 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${loraBadge.className}`}
                        >
                          {loraBadge.label}
                        </span>
                      );
                    })()}
                  </div>

                  <div>
                  <p className="text-xs font-semibold uppercase text-cyan-700">
                    Macchina
                  </p>
                  <p className="mt-1 text-sm text-gray-700">
                    {latestDevice.machineRef?.nome || '-'}
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold uppercase text-cyan-700">
                    Cliente
                  </p>
                  <p className="mt-1 text-sm text-gray-700">
                    {getMachineCustomerLabel(latestDevice.machineRef)}
                  </p>
                </div>
              </div>


              <div className="mt-6">
                <h3 className="text-sm font-semibold uppercase tracking-wide text-cyan-700 mb-3">
                  Sensori rilevati
                </h3>
                {renderMeasures(latestDevice.last_measures)}
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openAssignForm(latestDevice)}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition"
                >
                  Completa associazione
                </button>

              </div>
            </div>
          )}
        </Card>

        <Card className="shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            Dispositivi registrati
          </h2>

          {loadingDevices && (
            <p className="text-sm text-gray-500">Caricamento dispositivi...</p>
          )}

          {!loadingDevices && devicesError && (
            <p className="text-sm text-red-600">{devicesError}</p>
          )}

          {!loadingDevices && !devicesError && devices.length === 0 && (
            <p className="text-sm text-gray-500">
              Nessun dispositivo registrato.
            </p>
          )}

          {!loadingDevices && !devicesError && devices.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border-separate border-spacing-y-2">
                <thead>
                  <tr className="text-gray-500 text-left">
                    <th className="px-4 py-2">Device ID</th>
                    <th className="px-4 py-2">Reparto</th>
                    <th className="px-4 py-2">Associazioni</th>
                    <th className="px-4 py-2">Cliente</th>
                    <th className="px-4 py-2">Luogo</th>
                    <th className="px-4 py-2">Camera</th>
                    <th className="px-4 py-2">Matricola frigo</th>
                    <th className="px-4 py-2">Tecnico</th>
                    <th className="px-4 py-2">Sensori</th>
                    <th className="px-4 py-2">Stato</th>
                    <th className="px-4 py-2">IP</th>
                    <th className="px-4 py-2">Ultimo contatto</th>
                    <th className="px-4 py-2">Azione</th>
                  </tr>
                </thead>

                <tbody>
                  {devices.map((device) => (
                    <tr key={device._id || device.device_id} className="bg-gray-50">
                      <td className="px-4 py-3 rounded-l-xl font-medium text-gray-800">
                        {device.device_id}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-700">
                          {getDeviceDepartmentsLabel(device)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {Array.isArray(device.channels) && device.channels.length > 0 ? (
                          <div className="space-y-1">
                          {device.channels
                            .filter((channel) => channel.enabled !== false)
                            .map((channel) => (
                              <div key={channel.sensor_id} className="text-xs leading-5">
                                <span className="font-semibold">
                                  {channel.label || channel.sensor_id}
                                </span>
                                {' → '}
                                {(() => {
                                  const machine = getMachineByRef(channel.machineRef);
                                  const departmentName = getDepartmentNameFromMachine(machine);

                                  return (
                                    <>
                                      {machine?.nome || channel.machineRef?.nome || '-'}
                                      {departmentName ? ` / ${departmentName}` : ''}
                                    </>
                                  );
                                })()}
                              </div>
                            ))}
                          </div>
                        ) : (
                          device.machineRef?.nome || '-'
                        )}
                      </td>

                      <td className="px-4 py-3 text-gray-700">
                        {getMachineCustomerLabel(device.machineRef)}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.installation_site || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.room || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.fridge_serial || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.installed_by || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {Array.isArray(device.last_measures) &&
                        device.last_measures.filter((measure) =>
                          /^(TEMP|HUM)-\d+$/.test(normalizeSensorId(measure?.sensor_id))
                        ).length > 0 ? (
                          <div className="space-y-1">
                            {device.last_measures
                              .filter((measure) =>
                                /^(TEMP|HUM)-\d+$/.test(normalizeSensorId(measure?.sensor_id))
                              )
                              .map((measure, index) => {
                                const safeMeasure = measure || {};
                                return (
                                  <div
                                    key={`${safeMeasure.sensor_id || 'sensor'}-${index}`}
                                    className="text-xs leading-5"
                                  >
                                    <span className="font-medium">
                                      {safeMeasure.sensor_id || 'Sensore'}
                                    </span>
                                    {' · '}
                                    {safeMeasure.sensor_type || '-'}
                                    {' · '}
                                    {safeMeasure.value !== undefined && safeMeasure.value !== null
                                      ? String(safeMeasure.value)
                                      : '-'}
                                    {safeMeasure.unit ? ` ${safeMeasure.unit}` : ''}
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="flex flex-col gap-2">
                          {(() => {
                            const pairingBadge = getPairingBadge(device.pairing_status);
                            return (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${pairingBadge.className}`}
                              >
                                {pairingBadge.label}
                              </span>
                            );
                          })()}

                          {(() => {
                            const loraBadge = getLoraCommandBadge(device);
                            return (
                              <span
                                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${loraBadge.className}`}
                              >
                                {loraBadge.label}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.ip_address || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {device.last_seen_at
                          ? new Date(device.last_seen_at).toLocaleString()
                          : '-'}
                      </td>
                        <td className="px-4 py-3 rounded-r-xl">
                          <div className="flex flex-col gap-2">
                            <button
                              type="button"
                              onClick={() => openAssignForm(device)}
                              className="px-3 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-xs font-medium transition"
                            >
                              Associa / Modifica
                            </button>

                            <button
                              type="button"
                              onClick={() => handleUnassign(device)}
                              className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-xs font-medium transition"
                            >
                              Disassocia
                            </button>

                            <button
                              type="button"
                              onClick={() => handleReconfigure(device)}
                              className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-xs font-medium transition"
                            >
                              Riconfigura
                            </button>
                         
                            <button
                              type="button"
                              onClick={() => handleReboot(device)}
                              className="px-3 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-xs font-medium transition"
                            >
                              Riavvia
                            </button>
                          </div>
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {selectedDevice && (
          <Card className="shadow-md p-6">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  Associazione dispositivo
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  Stai configurando il device{' '}
                  <strong>{selectedDevice.device_id}</strong>
                </p>
              </div>

              <button
                type="button"
                onClick={closeAssignForm}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 text-sm font-medium transition"
              >
                Chiudi
              </button>
            </div>

            {isAdmin && (
              <div className="mt-5 flex flex-wrap gap-2 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => setDeviceTab('assign')}
                  className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${
                    deviceTab === 'assign'
                      ? 'border-cyan-600 text-cyan-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Associazione e canali
                </button>

                <button
                  type="button"
                  onClick={() => setDeviceTab('console')}
                  className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition ${
                    deviceTab === 'console'
                      ? 'border-cyan-600 text-cyan-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Console seriale
                </button>
              </div>
            )}

            {isAdmin && deviceTab === 'console' && (
              <div className="mt-6">
                <DeviceSerialConsole deviceId={selectedDevice.device_id} />
              </div>
            )}

            {/*
              Il form viene nascosto, non smontato: passando alla console e
              tornando indietro le modifiche non salvate restano dove sono.
            */}
            <form
              onSubmit={handleAssignSubmit}
              className={`mt-6 space-y-4 ${
                isAdmin && deviceTab === 'console' ? 'hidden' : ''
              }`}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-cyan-700 mb-3">
                    Configurazione canali sonda
                  </h3>

                  <div className="grid gap-3">
                    {form.channels.map((channel, index) => (
                      <div
                        key={channel.sensor_id}
                        className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                          <div className="min-w-[90px]">
                            <p className="text-sm font-bold text-gray-800">
                              {channel.label}
                            </p>
                            <p className="text-xs text-gray-500">
                              {channel.sensor_id}
                            </p>
                          </div>

                          <label className="flex items-center gap-2 text-sm text-gray-700">
                            <input
                              type="checkbox"
                              checked={channel.enabled}
                              onChange={(e) =>
                                handleChannelChange(index, 'enabled', e.target.checked)
                              }
                            />
                            Attivo
                          </label>

                          <select
                            value={channel.machineRef}
                            onChange={(e) =>
                              handleChannelChange(index, 'machineRef', e.target.value)
                            }
                            disabled={!channel.enabled || loadingMachines}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white disabled:bg-gray-100"
                          >
                            <option value="">Seleziona macchina</option>
                            {machines.map((machine) => (
                              <option key={machine._id} value={machine._id}>
                                {machine.nome}
                                {machine.matricola ? ` - ${machine.matricola}` : ''}
                                {machine.departmentRef?.name
                                  ? ` / ${machine.departmentRef.name}`
                                  : ''}
                              </option>
                            ))}
                          </select>

                          <select
                            value={channel.cell_type}
                            onChange={(e) =>
                              handleChannelChange(index, 'cell_type', e.target.value)
                            }
                            disabled={!channel.enabled}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white disabled:bg-gray-100"
                          >
                            {CELL_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          <input
                            type="number"
                            step="0.01"
                            value={channel.calibration_offset}
                            onChange={(e) =>
                              handleChannelChange(index, 'calibration_offset', e.target.value)
                            }
                            disabled={!channel.enabled}
                            placeholder={`Offset ${channelUnit(channel.sensor_id)}`}
                            className="w-full lg:w-[130px] border border-gray-300 rounded-lg px-3 py-2 outline-none focus:ring-2 focus:ring-cyan-400 bg-white disabled:bg-gray-100"
                          />

                          <label className="flex items-center gap-2 text-xs text-gray-700 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={channel.calibration_enabled !== false}
                              onChange={(e) =>
                                handleChannelChange(index, 'calibration_enabled', e.target.checked)
                              }
                              disabled={!channel.enabled}
                            />
                            Taratura attiva
                          </label>

                        </div>

                        <p className="mt-2 text-xs text-gray-500">
                          Reparto:{' '}
                          <strong>
                            {channel.machineRef
                              ? getMachineDepartmentLabel(channel.machineRef)
                              : '-'}
                          </strong>
                        </p>
                          <p className="mt-1 text-xs text-gray-500">
                            Taratura:{' '}
                            <strong>
                              offset {Number(channel.calibration_offset || 0).toFixed(2)}{' '}
                              {channelUnit(channel.sensor_id)}
                            </strong>
                          </p>
                      </div>
                    ))}
                  </div>
                </div>
                <input
                  type="text"
                  name="installation_site"
                  value={form.installation_site}
                  onChange={handleChange}
                  placeholder="Luogo installazione"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
                />

                <input
                  type="text"
                  name="room"
                  value={form.room}
                  onChange={handleChange}
                  placeholder="Camera"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
                />

                <input
                  type="text"
                  name="fridge_serial"
                  value={form.fridge_serial}
                  onChange={handleChange}
                  placeholder="Matricola frigo"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
                />

                <input
                  type="text"
                  name="installed_by"
                  value={form.installed_by}
                  onChange={handleChange}
                  placeholder="Tecnico installatore"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
                />
              </div>

              <textarea
                name="installation_notes"
                value={form.installation_notes}
                onChange={handleChange}
                placeholder="Note installazione"
                className="w-full min-h-[120px] border border-gray-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-cyan-400"
              />

              {saveMessage && (
                <p className="text-sm text-green-600">{saveMessage}</p>
              )}

              {saveError && (
                <p className="text-sm text-red-600">{saveError}</p>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 text-sm font-medium transition disabled:opacity-60"
                >
                  {saving ? 'Salvataggio...' : 'Salva associazione'}
                </button>

                <button
                  type="button"
                  onClick={closeAssignForm}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-medium transition"
                >
                  Annulla
                </button>
              </div>
            </form>
          </Card>
        )}
      </div>
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <Card className="w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-6">
              <div
                className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${
                  confirmModal.danger
                    ? 'bg-red-100 text-red-600'
                    : 'bg-cyan-100 text-cyan-600'
                }`}
              >
                !
              </div>

              <h3 className="text-xl font-bold text-gray-900 text-center">
                {confirmModal.title}
              </h3>

              <p className="mt-3 text-sm text-gray-600 text-center leading-6">
                {confirmModal.message}
              </p>

              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={closeConfirmModal}
                  className="flex-1 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition"
                >
                  Annulla
                </button>

                <button
                  type="button"
                  onClick={runConfirmedAction}
                  disabled={saving}
                  className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition disabled:opacity-60 ${
                    confirmModal.danger
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-cyan-600 hover:bg-cyan-700'
                  }`}
                >
                  {saving ? 'Attendere...' : confirmModal.confirmText}
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
    
  );
}